import { ReplaySubject } from "rxjs";
import { HelpDeskTicket } from "src/app/business/help-desk/help-desk-ticket";
import { StringUtilityService } from "src/app/system/utility/string-utility.service";
import { ChatMessage, ChatMessageDisplay, ChatRole, SystemMessageType } from "./chat-message";
import { ChatContextService } from "./chat-context.service";

export class ChatUtility {

  public static getDateDisplay(d: Date): string {
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
  }

  public static createChatMessageDisplay(srvChatContext : ChatContextService, msg: ChatMessage): ChatMessageDisplay {
    let date: Date = new Date(msg.timestamp!);
    let isMine = ((msg.senderId == srvChatContext.userName) || (msg.senderId == srvChatContext.alias));

    let hasRead: boolean = false;
    if (msg.unread !== undefined) hasRead = msg.unread;
    if (msg.read !== undefined) hasRead = msg.read;
    if (msg.history) hasRead = true;

    let subjImage: ReplaySubject<string> = new ReplaySubject<string>();

    let imageData = msg.imageData;
    let imageUrl = '';

    if (imageData) {
      if (imageData.startsWith('data')) {
        subjImage.next(imageData)
      } else if (!imageData.startsWith('data') && !msg.isPdf) {

        // Download Blob and then conert to base64
        let blobSubject = new ReplaySubject<Blob | null>();
        srvChatContext.downloadFile(imageData, blobSubject);
        
        blobSubject.subscribe((blob: Blob | null) => {
          if (blob) {
            let reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => {
              if (!reader.result) return;
              let base64Data: string = reader.result as string;
              subjImage.next(base64Data);
            }
          }
        });

      } else if (!imageData.startsWith('data') && msg.isPdf) {
        imageUrl = imageData;
      }
    }

    let result: ChatMessageDisplay = {
      messageId: +msg.messageId!,
      isLocalOnly: !msg.fromServer,
      hasRead: hasRead,
      hasSent: true,
      dateDisplay: ChatUtility.getDateDisplay(date),
      from: msg.senderName!,
      localId: msg.localId ? msg.localId : '',
      hasDelivered: msg.delivered ?? false,
      content: msg.content!,
      isMine: isMine,
      inQueue: false,
      isImage: msg.imageData !== undefined,
      imageName: msg.imageName ? msg.imageName : '',
      imageData: subjImage,
      imageUrl: imageUrl,
      isPdf: msg.isPdf,
      chatRole: isMine ? ChatRole.CUSTOMER : ChatRole.EMPLOYEE,
    }

    return result;
  }

  /**
    The create message is needed before the Help Desk Ticket is created on the server,
    so we will create a temporary one that will be replaced when the server socket
    tells us that this ticket is open and delivered.
  */
  public static buildCreateMessage(srvChatContext: ChatContextService, content: string, options: any = {}): ChatMessageDisplay {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    let encoded =  tempDiv.textContent || '';

    let result: ChatMessageDisplay = {
      from: srvChatContext.displayName,
      localId: '',
      isLocalOnly: true,
      messageId: -3,
      isMine: true,
      content: encoded,
      hasDelivered: options.hasDelivered ?? false,
      hasRead: options.hasRead ?? false,
      hasSent: options.hasSent ?? true,
      dateDisplay: options.dateDisplay ?? this.getDateDisplay(new Date()),
      inQueue: false,
      isImage: false,
      imageName: '',
      imageUrl: '',
      chatRole: ChatRole.CUSTOMER,
    };

    return result;
  }

  /**
    The Queue message is not created by the server (or stored by the server) 
    so we create a local one that lets the user know that they are waiting.

    The queue position will be updated by a socket message.
  */
  public static buildQueueMessage(srvChatContext: ChatContextService, ticket: HelpDeskTicket, options: any = {},): ChatMessageDisplay {

    let msgQueue = (ticket.queuePosition < 1) ? srvChatContext.instant('CHAT.EMPLOYEE_RIGHT_AWAY') : srvChatContext.instant('CHAT.QUEUE_MESSAGE', { position: ticket.queuePosition });
    let result: ChatMessageDisplay = {
      from: srvChatContext.instant('CHAT.FROM_SERVER'),
      localId: '',
      isLocalOnly: true,
      messageId: -2,
      isMine: false,
      content: msgQueue,
      hasDelivered: true,
      hasRead: true,
      hasSent: true,
      dateDisplay: options.dateDisplay ?? this.getDateDisplay(new Date()),
      inQueue: options.inQueue ?? true,
      isImage: false,
      imageName: '',
      imageUrl: '',
      sysMsgType: SystemMessageType.SYSTEM_THANKS,
      chatRole: ChatRole.SYSTEM,
    }

    return result;
  }

  public static buildEndChatMessage(srvChatContext: ChatContextService, ticket: HelpDeskTicket, content: string, options: any = {}): ChatMessageDisplay {

    let result: ChatMessageDisplay = {
      from: srvChatContext.instant('CHAT.FROM_SERVER'),
      localId: '',
      isLocalOnly: true,
      messageId: -2,
      isMine: false,
      content: srvChatContext.instant(content),
      hasDelivered: true,
      hasRead: true,
      hasSent: true,
      dateDisplay: options.dateDisplay ?? ticket.lastUpdated,
      inQueue: false,
      isImage: false,
      imageName: '',
      imageUrl: '',
      sysMsgType: SystemMessageType.SYSTEM_END_CHAT,
      chatRole: ChatRole.SYSTEM,
    };

    return result;
  }

  public static buildJoinedChatMessage(srvChatContext: ChatContextService, ticket: HelpDeskTicket, msgChat: ChatMessage, options: any = {}): ChatMessageDisplay {
    let result: ChatMessageDisplay = {
      from: srvChatContext.instant('CHAT.FROM_SERVER'),
      localId: '',
      isLocalOnly: true,
      messageId: -2,
      isMine: false,
      content: srvChatContext.instant('CHAT.HAS_JOINED', { senderName: msgChat.senderName }),
      hasDelivered: true,
      hasRead: true,
      hasSent: true,
      dateDisplay: options.dateDisplay ?? ticket.lastUpdated,
      inQueue: false,
      isImage: false,
      imageName: '',
      imageUrl: '',
      sysMsgType: SystemMessageType.SYSTEM_JOINED_CHAT,
      chatRole: ChatRole.SYSTEM,
    }

    return result;
  }

  public static buildRouteMessage(srvChatContext: ChatContextService, ticket: HelpDeskTicket, msgChat: ChatMessage): ChatMessageDisplay {
    let date: Date = new Date(msgChat.timestamp!);

    let content = srvChatContext.instant('CHAT.ROUTED_MESSAGE_1') + srvChatContext.instant('CHAT.ROUTED_MESSAGE_2', { senderName: msgChat?.senderName || 'User' });
    let result: ChatMessageDisplay = {
      from: srvChatContext.instant('CHAT.FROM_SERVER'),
      localId: '',
      isLocalOnly: true,
      messageId: -2,
      isMine: false,
      content: content,
      hasDelivered: true,
      hasRead: true,
      hasSent: true,
      dateDisplay: this.getDateDisplay(date),
      inQueue: false,
      isImage: false,
      imageName: '',
      imageUrl: '',
      sysMsgType: SystemMessageType.SYSTEM_ROUTED_CHAT,
      chatRole: ChatRole.SYSTEM,
    }

    return result;
  }

  public static serializeSocketMessage(msg: ChatMessage): string {
    let addHeader = (hdr: string[], field: any, name: string, value: any = field) => {
      if (field !== undefined) hdr.push(name + ':' + value);
    }

    let hdr: string[] = [];
    addHeader(hdr, msg.channel, 'ch');
    addHeader(hdr, msg.delivered, 'delivered', true);
    addHeader(hdr, msg.helpDeskEvent, 'hdevent');
    addHeader(hdr, msg.helpDeskTicketId, 'hdid');
    addHeader(hdr, msg.localId, 'localId');
    addHeader(hdr, msg.pm, 'pm', msg.pm);
    addHeader(hdr, msg.senderName, 'senderName');
    addHeader(hdr, msg.status, 'status');
    addHeader(hdr, msg.timestamp, 'timestamp');
    addHeader(hdr, msg.unread, 'unread');
    addHeader(hdr, msg.messageId, 'messageId');
    addHeader(hdr, msg.imageName, 'attachment-filename')
    addHeader(hdr, msg.imageData, 'attachment');
    addHeader(hdr, msg.read, 'read');
    let header = hdr.join(';');
    let content = msg.content ?? '';
    return header + '#' + content;
  }

  public static parseSocketMessage(msg: string): ChatMessage {
    let strChatItemMsg = msg.split('#');
    let strChatItemHeader = strChatItemMsg[0];
    let strChatItemContent = strChatItemMsg[1];

    let header: Record<string, string> = {};
    let arrHeaders = strChatItemHeader.split(";")
    arrHeaders.forEach((hdr: string) => {
      let detail = hdr.split(':')
      if (detail.length == 2) {
        let name = detail[0];
        let value = detail[1];
        header[name] = value;
      }
    })

    let attachmentName = header['attachment-filename'];

    let ret: ChatMessage = {
      history: header['history'] === 'true',
      unread: header['unread'] === undefined ? undefined : header['unread'] == 'true',
      read: header['read'] === undefined ? undefined : header['read'] == 'true',
      messageId: header['messageId'] ? header['messageId'] : '',
      timestamp: header['timestamp'] ? +header['timestamp'] : 0,
      pm: header['pm'] ? header['pm'] : '',
      gm: header['gm'] ? header['gm'] : '',
      senderId: header['senderId'] ? header['senderId'] : '',
      senderName: header['senderName'] ? header['senderName'] : '',
      delivered: header['delivered'] === undefined ? undefined : header['delivered'] == 'true',
      status: header['status'] ? +header['status'] : -1,
      channel: header['ch'] ? header['ch'] : '',
      helpDeskEvent: header['hdevent'] ? +header['hdevent'] : -1,
      helpDeskTicketId: header['hdid'] ? +header['hdid'] : -1,
      localId: header['localId'] ? header['localId'] : '',
      content: StringUtilityService.normalizeLineBreak(strChatItemContent),
      fromServer: true,
      imageData: header['attachmentUrl'] ?? undefined,
      imageName: attachmentName === undefined ? undefined : header['attachment-filename'],
      isPdf: attachmentName?.includes('.pdf'),
      qp: header['qp'] === undefined ? undefined : +header['qp']
    }

    return ret;
  }



}